import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import styles from "./styles.module.scss";
import { FC, Props } from "./typings";
import FlagIcon from "assets/icons/flag_border.svg?react";
import { faCaretDown, faCaretUp } from "@fortawesome/free-solid-svg-icons";
import CloseIcon from "assets/icons/close.svg?react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import statusService from "api/statusService";
import { IStatus } from "dto/IStatus";
import StatusBadge from "components/StatusBadge";
import { useOnClickOutside } from "usehooks-ts";
import { getCompanyId as getWorkspaceId } from "utils/getCompanyId";
import DropdownLogic from "components/DropdownLogic";

import EditIcon from "assets/icons/mode_edit_24px.svg?react";
import Input from "components/Input";
import NewInput from "components/NewInput";
import HollowButton from "components/HollowButton";
import Button from "components/Button";
import classNames from "classnames";

const SelectFlagNew: FC<Props> = ({
  onChoose,
  onClose,
  selectedStatus,
  initOpen = false,
  listType,
  disableControl = false,
  allowSearch = true,
  showButtons = false,
  showCounter = false,
  selectedStatusId,
  showChoosen = false,
}) => {
  const { t } = useTranslation();
  const [active, setActive] = useState(false);
  const [value, setValue] = useState("");
  const currentWorkspaceId = getWorkspaceId() ?? "";
  const { data: statusesData, refetch } = statusService.useGetStatuses(
    currentWorkspaceId,
    listType,
  );

  const { mutate: updateStatus } = statusService.useUpdateStatus();
  const statuses: IStatus[] = useMemo(
    () => statusesData?.data ?? [],
    [statusesData],
  );

  const [search, setSearch] = useState<IStatus[]>([]);

  const [editStatus, setEditStatus] = useState<IStatus | null>(null);
  const [internalSelected, setInternalSelected] = useState<IStatus | null>(
    null,
  );

  const handleSaveEditStatus = () => {
    updateStatus(editStatus, {
      onSuccess: () => {
        refetch();
        setEditStatus(null);
      },
    });
  };

  useEffect(() => {
    if (!initOpen) return;
    setActive(true);
  }, [initOpen]);

  const outsideRef = useRef(null);

  useOnClickOutside(outsideRef, () => {
    if (active) {
      setActive(false);
    }

    if (onClose && selectedStatus === null) {
      onClose();
    }
  });

  useEffect(() => {
    if (!value.length) {
      setSearch(statuses);
      return;
    }

    setSearch(
      statuses.filter((tag: IStatus) =>
        tag.Name.toLocaleLowerCase().includes(value.toLocaleLowerCase()),
      ),
    );
  }, [statuses, value]);

  useEffect(() => {
    if (selectedStatusId) {
      setInternalSelected(statuses.find((x) => x.Id === selectedStatusId));
    }
  }, [statuses]);

  const clear = () => {
    onChoose(null);
    setActive(null);
  };

  const save = () => {
    onChoose(internalSelected);
    setActive(false);
  };

  return (
    <div ref={outsideRef} className={styles.wrapper}>
      <div
        onClick={() => setActive(!active)}
        className={`${styles.card} ${disableControl && styles.hide}`}
      >
        {selectedStatus ? (
          <StatusBadge onRemove={() => {}} status={selectedStatus} />
        ) : (
          <FlagIcon className={`${styles.icon}`} />
        )}{" "}
        {selectedStatus ? (
          <div className={`${styles.text}`}>{selectedStatus.Name}</div>
        ) : (
          <>
            <div className={styles.text}>{t("common.status")}</div>
            {showCounter && selectedStatusId && (
              <div className={styles.counter}>1</div>
            )}
          </>
        )}
        {selectedStatus && (
          <CloseIcon
            onClick={() => {
              onChoose(null);
              onClose?.();
            }}
          />
        )}
        {!onClose && (
          <FontAwesomeIcon
            className={styles.closeIcon}
            icon={active ? faCaretUp : faCaretDown}
          />
        )}
      </div>

      {active && (
        <DropdownLogic>
          <div className={styles.dropdown}>
            {allowSearch && (
              <div className={styles.search}>
                <NewInput
                  focus
                  labelClassName={styles.x}
                  className={styles.d}
                  value={value}
                  label={t("statuses.searchStatus")}
                  onChange={(v) => setValue(v)}
                />
              </div>
            )}
            {showChoosen && internalSelected && (
              <StatusBadge
                status={internalSelected}
                onRemove={() => onChoose(null)}
                showName={true}
              ></StatusBadge>
            )}
            <div className={styles.list}>
              {search.map((status) => (
                <div
                  key={status.Id}
                  className={classNames(styles.box)}
                  onClick={() => {
                    if (editStatus?.Id === status.Id) {
                      return;
                    }

                    onChoose(status);
                    setActive(false);
                    setInternalSelected(status);
                  }}
                >
                  {editStatus?.Id !== status.Id ? (
                    <>
                      <div className={styles.name}>
                        <StatusBadge onRemove={() => {}} status={status} />
                        {status.Name}
                      </div>
                      <EditIcon
                        onClick={(e) => {
                          setEditStatus(status);
                          e.preventDefault();
                          e.stopPropagation();
                        }}
                        className={styles.icone}
                      />
                    </>
                  ) : (
                    <Input
                      value={editStatus.Name}
                      label={t("statuses.editStatus")}
                      onRightText={t("common.save")}
                      onRightClick={handleSaveEditStatus}
                      onChange={(t) => {
                        const newStatus = { ...editStatus };
                        newStatus.Name = t;
                        setEditStatus(newStatus);
                      }}
                    />
                  )}
                </div>
              ))}
            </div>
            {showButtons && (
              <div className={styles.buttons}>
                <HollowButton
                  text={t("common.clear")}
                  onClick={clear}
                ></HollowButton>
                <Button text={t("common.save")} onClick={save}></Button>
              </div>
            )}
          </div>
        </DropdownLogic>
      )}
    </div>
  );
};

export default SelectFlagNew;
